import { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Button,
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'
import { useCurrentUser } from 'common/hooks'
import { automationsPaths } from 'Automations/router'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'
import { match } from 'ts-pattern'

type Props = {
  quoteId: string
}

export const ExecuteQuoteAction: FC<Props> = ({ quoteId }) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const { notificationId } = useParams<{ notificationId: string }>()
  const currentUser = useCurrentUser()

  const executeMutation = useMutation(dashboard20240730Client.postQuoteExecute)

  // A successful execution resolves the notification, so dismiss it as if the
  // "Done" button was pressed: complete the manual task and return to the inbox.
  const completeTaskMutation = useMutation(
    dashboardv99990101Client.postCompleteManualTask,
    {
      onSuccess: () => navigate(automationsPaths.manualTasksInbox()),
      onError: () =>
        displayNotification('Failed to dismiss notification', { type: 'error' })
    }
  )

  const quoteQuery = useQuery(
    dashboard20240730Client.getQuote,
    { id: quoteId },
    {
      select: data => ({ status: data?.status, dealType: data?.dealType }),
      staleTime: 1 * MINUTES
    }
  )
  // Reflect the quote's actual status so the button stays disabled after a
  // refresh once it has been executed; isSuccess covers the window between
  // executing and the invalidated QUOTES query refetching.
  const isExecuted =
    executeMutation.isSuccess || quoteQuery.data?.status === 'EXECUTED'

  const dealType = quoteQuery.data?.dealType
  const execution = match(dealType)
    .with('AMENDMENT', () => ({
      scheduleAction: { mode: 'AMEND_EXISTING' as const },
      buttonText: 'Execute quote',
      executedButtonText: 'Quote executed',
      title: 'Execute quote',
      body: "You're about to amend the customer's active billing schedule with this quote. The changes apply straight away and credit notes may be raised.",
      successMessage: 'Billing schedule amended',
      errorMessage: 'Failed to amend billing schedule'
    }))
    .otherwise(() => ({
      scheduleAction: { mode: 'CREATE_NEW' as const },
      buttonText: 'Create billing schedule',
      executedButtonText: 'Billing schedule created',
      title: 'Create billing schedule',
      body: 'You’re about to convert this quote into a draft billing schedule.',
      successMessage: 'Draft billing schedule created',
      errorMessage: 'Failed to convert quote'
    }))

  return (
    <Flex gap="8px" alignItems="center" width="100%">
      <SimpleModal
        trigger={
          <Button
            variant="secondary"
            style={{ flex: 1 }}
            disabled={isExecuted || !dealType}
            data-testid="watchtower.notification.executeQuote"
          >
            {isExecuted ? execution.executedButtonText : execution.buttonText}
          </Button>
        }
        variant="dialog"
        title={execution.title}
        submitButtonText={execution.buttonText}
        data-testid="watchtower.notification.executeQuote.modal"
        onSubmit={async () => {
          try {
            const executedQuote = await executeMutation.mutateAsync({
              id: quoteId,
              body: { scheduleAction: execution.scheduleAction }
            })

            const billingScheduleId = executedQuote?.billingScheduleId
            displayNotification(execution.successMessage, {
              type: 'success',
              truncate: false,
              'data-testid':
                'watchtower.notification.executeQuote.successToast',
              ...(billingScheduleId && {
                viewLabel: 'View schedule',
                onClickView: () =>
                  navigate(`/billing-schedules/${billingScheduleId}`)
              })
            })

            if (notificationId) {
              completeTaskMutation.mutate({
                id: notificationId,
                outputData: { completedBy: currentUser.id }
              })
            }

            return true
          } catch (error) {
            displayNotification(
              getApiErrorMessage(error, execution.errorMessage),
              { type: 'error' }
            )
            return false
          }
        }}
      >
        {execution.body}
      </SimpleModal>
    </Flex>
  )
}
